import type { Reference, StoreObject, TypePolicies } from '@apollo/client';

// Apollo InMemoryCache type policies for the trace UI, isolated in their own
// module (Apollo-only imports, no links / side effects) so the pagination
// merge logic can be exercised in isolation — traceClient.ts pulls in
// errorLink → siebel → @stratumn/gql-constants, which a unit test can't
// resolve. traceClient.ts imports these into its InMemoryCache.
export const typePolicies: TypePolicies = {
  Workflow: {
    keyFields: ['rowId'],
    fields: {
      fastTraceStates: {
        // Only filter + orderBy determine which "list" we're reading.
        // Pagination args (first / after / cursor) are intentionally excluded
        // so that all pages for the same filter share a single cache entry.
        keyArgs: ['filter', 'orderBy'],
        merge(existing, incoming, { args }) {
          // If a cursor is present this is a fetchMore — append to existing nodes.
          // Otherwise (initial fetch or filter change) replace entirely.
          if (args?.after) {
            return {
              ...incoming,
              nodes: [...(existing?.nodes ?? []), ...(incoming.nodes ?? [])]
            };
          }
          return incoming;
        }
      }
    }
  },
  Trace: {
    fields: {
      links: {
        // All pages of a trace's links share one cache entry regardless of
        // pagination args. `filter` is kept in the key so filtered sub-lists
        // don't pollute the unfiltered one.
        keyArgs: ['filter'],
        merge(existing, incoming, { readField }) {
          // The ribbon addresses links by ABSOLUTE INDEX (height - 1) and
          // renders any unloaded index as a skeleton, so windows never need to
          // be contiguous: we ALWAYS union by identity and sort height DESC. A
          // far, non-adjacent offset fetch simply coexists with earlier windows
          // and the gap between them is unloaded indices an offset fetch fills
          // on demand. (This replaced an offset>0 REPLACE branch that only
          // existed because cursor pagination couldn't tolerate a gap.)
          type LinkNode = Reference | StoreObject;
          const merged = new Map<string, LinkNode>();
          const add = (node: LinkNode) => {
            // Nodes are normalized refs — key by ref when possible,
            // otherwise fall back to the linkHash scalar.
            const ref = (node as Reference).__ref;
            const key =
              ref ?? (readField('linkHash', node) as string | undefined);
            if (key) merged.set(key, node);
          };
          for (const n of (existing?.nodes ?? []) as LinkNode[]) add(n);
          for (const n of (incoming?.nodes ?? []) as LinkNode[]) add(n);
          // Sort by height DESC so subscription-injected heads (and any
          // out-of-order window) land in absolute-height order.
          const nodes = Array.from(merged.values()).sort((a, b) => {
            const ha = (readField('height', a) as number) ?? 0;
            const hb = (readField('height', b) as number) ?? 0;
            return hb - ha;
          });
          // Preserve totalCount across partial writes. The trace subscription
          // writes links(first: 1) with only `nodes` selected — no totalCount —
          // and totalCount is the virtualizer's item count, so dropping it to
          // undefined would collapse the whole ribbon. Keep the incoming value
          // when present, otherwise fall back to what we already have.
          const totalCount = incoming?.totalCount ?? existing?.totalCount;
          // pageInfo is no longer consumed by the ribbon (offset fetches don't
          // use cursors), but keep whichever side each write describes so any
          // future cursor reader stays correct.
          const pageInfo = {
            ...(existing?.pageInfo ?? {}),
            ...(incoming?.pageInfo ?? {})
          };
          return { ...existing, ...incoming, totalCount, pageInfo, nodes };
        }
      }
    }
  }
};
